import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "kpkl-np-notifications";

export type NowPlayingTrack = {
  artist: string;
  title: string;
};

export type StationInfo = {
  id: string;
  name: string;
};

/**
 * Now-Playing web notifications (issue #48).
 *
 * When the listener's station changes — or the next track starts — the
 * service worker surfaces a notification with the new now-playing track.
 * Notifications only fire while the tab is in the background (hidden), so
 * they never shout over the UI the user is actively looking at.
 *
 * Opt-in via `enable()` (user gesture → permission prompt), preference is
 * persisted in localStorage.
 */
export function useNowPlayingNotifications({
  station,
  nowPlaying,
  isPlaying,
}: {
  station: StationInfo | null;
  nowPlaying: NowPlayingTrack | null;
  isPlaying: boolean;
}) {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const supported =
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator;

  const permission: NotificationPermission | "unsupported" = supported
    ? Notification.permission
    : "unsupported";

  const enabledRef = useRef(enabled);

  /** Last track we already announced, so polls don't re-notify. */
  const lastNotifiedRef = useRef<string | null>(null);

  const persist = useCallback((value: boolean) => {
    enabledRef.current = value;
    setEnabled(value);
    try {
      localStorage.setItem(STORAGE_KEY, value ? "1" : "0");
    } catch {
      /* private mode — session-only preference */
    }
  }, []);

  const enable = useCallback(async (): Promise<boolean> => {
    if (!supported) return false;
    let perm = Notification.permission;
    if (perm === "default") {
      try {
        perm = await Notification.requestPermission();
      } catch {
        return false;
      }
    }
    if (perm !== "granted") return false;
    persist(true);
    return true;
  }, [supported, persist]);

  const disable = useCallback(() => {
    persist(false);
  }, [persist]);

  const stationId = station?.id;
  const stationName = station?.name;
  const artist = nowPlaying?.artist;
  const title = nowPlaying?.title;

  // Announce track changes on the active station while in the background.
  useEffect(() => {
    if (!enabled || !supported) return;
    if (Notification.permission !== "granted") return;
    if (
      stationId === undefined ||
      stationName === undefined ||
      artist === undefined ||
      title === undefined ||
      !isPlaying
    )
      return;
    // Only notify when the user can't see the tab — the visible UI already
    // shows the now-playing track.
    if (document.visibilityState !== "hidden") return;

    const trackKey = `${stationId}|${artist}|${title}`;
    if (trackKey === lastNotifiedRef.current) return;
    let disposed = false;
    const isCurrent = () =>
      !disposed &&
      enabledRef.current &&
      Notification.permission === "granted" &&
      document.visibilityState === "hidden";

    const body = artist ? `${artist} — ${title}` : title;

    const options: NotificationOptions = {
      body,
      tag: `kpkl-np-${stationId}`,
      icon: "/icon-512.png",
      badge: "/icon-192.png",
      data: { url: "/" },
    };

    // Prefer the SW registration (works from installed PWAs / background
    // tabs); fall back to the plain Notification constructor.
    navigator.serviceWorker
      .getRegistration()
      .then(reg => {
        if (!isCurrent()) return;
        lastNotifiedRef.current = trackKey;
        if (reg) return reg.showNotification(stationName, options);
        new Notification(stationName, options);
      })
      .catch(() => {
        if (!isCurrent()) return;
        try {
          lastNotifiedRef.current = trackKey;
          new Notification(stationName, options);
        } catch {
          /* notification failures are non-fatal */
        }
      });
    return () => {
      disposed = true;
    };
  }, [enabled, supported, stationId, stationName, artist, title, isPlaying]);

  return { supported, enabled, permission, enable, disable };
}
